import { Divider, showToast } from '@frontend/web-react';
import { useCreateSetupIntentMutation, useSetupIntentConfirmedMutation } from '@frontend/web/hooks/api';
import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type {
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import { useState } from 'react';
import { stripeExpressCheckoutOptions } from '../Payment.constants';

type BuyCreditsExpressCheckoutProps = {
  onComplete: () => void;
};

export const BuyCreditsExpressCheckout = ({ onComplete }: BuyCreditsExpressCheckoutProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [setupIntentConfirmed] = useSetupIntentConfirmedMutation();
  const [isAvailable, setIsAvailable] = useState(false);

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      event.paymentFailed({ reason: 'fail' });
      return;
    }
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        event.paymentFailed({ reason: 'fail' });
        return;
      }
      const setupIntentResult = await createSetupIntent().unwrap();
      const { error, setupIntent } = await stripe.confirmSetup({
        clientSecret: setupIntentResult.stripeClientSecret,
        elements,
        redirect: 'if_required',
      });
      if (error || !setupIntent) {
        event.paymentFailed({ reason: 'fail' });
        return;
      }
      await setupIntentConfirmed({ isDefault: true, setupIntentId: setupIntent.id, skipInvalidate: false }).unwrap();
      onComplete();
    } catch {
      event.paymentFailed({ reason: 'fail' });
      showToast({
        message: 'Payment failed. Please try again or use a card.',
        title: 'Payment failed',
        variant: 'danger',
      });
    }
  };

  if (!isAvailable) return null;

  return (
    <>
      <ExpressCheckoutElement
        onConfirm={onConfirm}
        onReady={({ availablePaymentMethods }: StripeExpressCheckoutElementReadyEvent) =>
          setIsAvailable(Boolean(availablePaymentMethods))
        }
        options={stripeExpressCheckoutOptions}
      />
      <Divider label="Or pay with card" labelPosition="center" />
    </>
  );
};
